import {
  ActionIcon, Badge, Button, Card, Checkbox, Divider, Grid, Group, MultiSelect, NumberInput, SegmentedControl, Select, Stack, Switch, Text,
  TextInput, Textarea, Title, Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { http } from "../../api/http";
import { useCan } from "../../auth/user";
import { Chart } from "../../components/Charts";
import { DataTable } from "../../components/DataTable";
import { useT } from "../../i18n/script";
import { initialParams, type ParamDef, ParamsForm, type Template } from "./ReportsPage";

interface SrcCol { field: string; label: string; type: string }
interface Source { code: string; title: string; columns: SrcCol[] }
interface OutCol { field?: string; expr?: string; agg?: string | null; label?: string; key?: string }
type Cond = { field: string; cmp: string; value?: unknown } | { op: "and" | "or"; items: Node[] } | { op: "not"; item: Node };
type Node = Cond;

const AGGS = [{ value: "", label: "—" }, { value: "sum", label: "Zbir" }, { value: "avg", label: "Prosjek" }, { value: "min", label: "Min" },
  { value: "max", label: "Max" }, { value: "count", label: "Broj" }, { value: "count_distinct", label: "Broj različitih" }];
const CMPS = [{ value: "=", label: "=" }, { value: "!=", label: "≠" }, { value: ">", label: ">" }, { value: ">=", label: "≥" }, { value: "<", label: "<" },
  { value: "<=", label: "≤" }, { value: "in", label: "u listi" }, { value: "not_in", label: "nije u listi" }, { value: "contains", label: "sadrži" },
  { value: "starts", label: "počinje sa" }, { value: "between", label: "između" }, { value: "is_null", label: "prazno" }, { value: "not_null", label: "nije prazno" }];

function outKey(c: OutCol, i: number): string {
  if (c.key) return c.key;
  if (c.expr) return `izraz_${i + 1}`;
  return c.agg ? `${c.field}_${c.agg}` : String(c.field);
}

function CondEditor({ node, cols, params, onChange, onRemove, depth = 0 }: {
  node: Node; cols: SrcCol[]; params: ParamDef[]; onChange: (n: Node) => void; onRemove?: () => void; depth?: number;
}) {
  const { t } = useT();
  if ("op" in node && node.op === "not") {
    return (
      <Card withBorder p="xs" bg="var(--mantine-color-red-light)">
        <Group justify="space-between" mb={4}><Badge color="red">NOT</Badge>
          <Button size="compact-xs" variant="subtle" onClick={() => onChange(node.item)}>{t("ukloni NOT")}</Button></Group>
        <CondEditor node={node.item} cols={cols} params={params} depth={depth + 1} onChange={(n) => onChange({ op: "not", item: n })} onRemove={onRemove} />
      </Card>
    );
  }
  if ("op" in node) {
    const g = node as { op: "and" | "or"; items: Node[] };
    return (
      <Card withBorder p="xs" style={{ borderLeft: `3px solid ${g.op === "and" ? "#2f9e44" : "#1c7ed6"}` }}>
        <Group justify="space-between" mb={6}>
          <SegmentedControl size="xs" value={g.op} onChange={(v) => onChange({ ...g, op: v as "and" | "or" })}
            data={[{ value: "and", label: t("SVI uslovi (AND)") }, { value: "or", label: t("BILO KOJI (OR)") }]} />
          <Group gap={4}>
            <Button size="compact-xs" variant="light" leftSection={<IconPlus size={12} />}
              onClick={() => onChange({ ...g, items: [...g.items, { field: cols[0]?.field ?? "", cmp: "=", value: "" }] })}>{t("uslov")}</Button>
            {depth < 4 && <Button size="compact-xs" variant="light" leftSection={<IconPlus size={12} />}
              onClick={() => onChange({ ...g, items: [...g.items, { op: g.op === "and" ? "or" : "and", items: [] }] })}>{t("grupa")}</Button>}
            {onRemove && <ActionIcon size="sm" color="red" variant="subtle" onClick={onRemove}><IconTrash size={12} /></ActionIcon>}
          </Group>
        </Group>
        <Stack gap={6}>
          {g.items.map((it, i) => (
            <Group key={i} gap={4} wrap="nowrap" align="flex-start">
              <Tooltip label={t("Negacija (NOT)")}><Button size="compact-xs" variant="subtle" color="red"
                onClick={() => onChange({ ...g, items: g.items.map((x, j) => (j === i ? { op: "not", item: x } : x)) })}>NOT</Button></Tooltip>
              <div style={{ flex: 1 }}>
                <CondEditor node={it} cols={cols} params={params} depth={depth + 1}
                  onChange={(n) => onChange({ ...g, items: g.items.map((x, j) => (j === i ? n : x)) })}
                  onRemove={() => onChange({ ...g, items: g.items.filter((_, j) => j !== i) })} />
              </div>
            </Group>
          ))}
          {!g.items.length && <Text size="xs" c="dimmed">{t("Prazna grupa (bez ograničenja)")}</Text>}
        </Stack>
      </Card>
    );
  }
  const c = node as { field: string; cmp: string; value?: unknown };
  const isParam = typeof c.value === "object" && c.value !== null && "param" in (c.value as object);
  const noVal = c.cmp === "is_null" || c.cmp === "not_null";
  return (
    <Group gap={4} wrap="nowrap">
      <Select size="xs" w={200} searchable data={cols.map((x) => ({ value: x.field, label: x.label }))} value={c.field} onChange={(v) => onChange({ ...c, field: v ?? "" })} />
      <Select size="xs" w={120} data={CMPS} value={c.cmp} onChange={(v) => onChange({ ...c, cmp: v ?? "=" })} />
      {!noVal && (isParam
        ? <Select size="xs" w={160} data={params.map((p) => ({ value: p.name, label: `${t("param")}: ${p.label}` }))}
          value={(c.value as { param: string }).param} onChange={(v) => onChange({ ...c, value: { param: v } })} />
        : <TextInput size="xs" w={190} placeholder={c.cmp === "in" || c.cmp === "not_in" || c.cmp === "between" ? t("vrijednosti zarezom") : t("vrijednost")}
          value={Array.isArray(c.value) ? c.value.join(", ") : String(c.value ?? "")}
          onChange={(e) => {
            const s = e.currentTarget.value;
            const list = ["in", "not_in", "between"].includes(c.cmp);
            const conv = (x: string) => (x.trim() !== "" && !Number.isNaN(Number(x)) ? Number(x) : x.trim());
            onChange({ ...c, value: list ? s.split(",").map(conv) : conv(s) });
          }} />)}
      {!noVal && params.length > 0 && <Tooltip label={t("Vrijednost iz parametra izvještaja")}>
        <Checkbox size="xs" checked={isParam} onChange={(e) => onChange({ ...c, value: e.currentTarget.checked ? { param: params[0].name } : "" })} /></Tooltip>}
      {onRemove && <ActionIcon size="sm" color="red" variant="subtle" onClick={onRemove}><IconTrash size={12} /></ActionIcon>}
    </Group>
  );
}

export function BuilderPanel({ template, onSaved }: { template: Template | null; onSaved: () => void }) {
  const { t } = useT();
  const can = useCan();
  const sources = useQuery({ queryKey: ["report-sources"], queryFn: () => http.get<Source[]>("/api/v1/reports/sources") });
  const full = useQuery({ queryKey: ["report-template", template?.code], enabled: !!template,
    queryFn: () => http.get<Template & { definition: Record<string, unknown> }>(`/api/v1/reports/templates/${template!.code}`) });
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [category, setCategory] = useState("Moji izvještaji");
  const [audience, setAudience] = useState("oba");
  const [shared, setShared] = useState(false);
  const [source, setSource] = useState<string | null>(null);
  const [columns, setColumns] = useState<OutCol[]>([]);
  const [filters, setFilters] = useState<Node>({ op: "and", items: [] });
  const [params, setParams] = useState<ParamDef[]>([{ name: "sezona", label: "Sezona", type: "integer", default: "current_year", required: true }]);
  const [periodField, setPeriodField] = useState<string | null>(null);
  const [orderBy, setOrderBy] = useState<{ field: string; dir: string }[]>([]);
  const [limit, setLimit] = useState<number | string>(5000);
  const [totals, setTotals] = useState(true);
  const [chart, setChart] = useState<{ type: string; x: string | null; series: string[] } | null>(null);
  const [pv, setPv] = useState<{ columns: { key: string; label: string; type: string }[]; rows: Record<string, unknown>[]; totals?: Record<string, unknown> } | null>(null);
  const [pvParams, setPvParams] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const d = full.data;
    if (!d) return;
    const def = d.definition as Record<string, unknown>;
    setName(d.name); setDesc(d.description ?? ""); setCategory(d.category); setAudience(d.audience); setShared(d.shared);
    setSource((def.source as string) ?? null); setColumns((def.columns as OutCol[]) ?? []);
    setFilters((def.filters as Node) ? ("op" in (def.filters as object) ? (def.filters as Node) : { op: "and", items: [def.filters as Node] }) : { op: "and", items: [] });
    setParams(d.params ?? []); setPeriodField((def.period_field as string) ?? null); setOrderBy((def.order_by as { field: string; dir: string }[]) ?? []);
    setLimit((def.limit as number) ?? 5000); setTotals(!!def.totals);
    const ch = def.chart as { type: string; x: string; series: string[] } | undefined;
    setChart(ch ? { type: ch.type, x: ch.x, series: ch.series } : null);
  }, [full.data]);
  useEffect(() => setPvParams(initialParams(params)), [params]);

  const src = sources.data?.find((s) => s.code === source);
  const cols = src?.columns ?? [];
  const outCols = columns.map((c, i) => ({ key: outKey(c, i), label: c.label || (c.expr ?? cols.find((x) => x.field === c.field)?.label ?? c.field ?? "") }));
  const definition = () => ({
    source, columns, filters: (filters as { items?: Node[] }).items?.length ? filters : undefined, period_field: periodField || undefined,
    order_by: orderBy.length ? orderBy : undefined, limit: Number(limit) || undefined, totals,
    chart: chart && chart.x && chart.series.length ? chart : undefined,
  });
  const preview = async () => {
    setBusy(true);
    try { setPv(await http.post("/api/v1/reports/preview", { definition: definition(), params: pvParams, limit: 500 })); }
    catch (e) { notifications.show({ color: "red", title: t("Definicija nije ispravna"), message: (e as Error).message }); setPv(null); }
    finally { setBusy(false); }
  };
  const save = async () => {
    try {
      const body = { name, description: desc || null, category, audience, params, definition: definition(), shared };
      if (template) await http.put(`/api/v1/reports/templates/${template.code}`, body);
      else await http.post("/api/v1/reports/templates", body);
      notifications.show({ color: "green", message: t("Izvještaj sačuvan") });
      onSaved();
    } catch (e) { notifications.show({ color: "red", message: (e as Error).message }); }
  };
  const setCol = (i: number, patch: Partial<OutCol>) => setColumns(columns.map((c, j) => (i === j ? { ...c, ...patch } : c)));
  const dateCols = cols.filter((c) => c.type === "date");

  return (
    <Grid>
      <Grid.Col span={{ base: 12, xl: 5 }}>
        <Stack>
          <Card withBorder>
            <Title order={6} mb="xs">1. {t("Izvor podataka")}</Title>
            <Select searchable data={(sources.data ?? []).map((s) => ({ value: s.code, label: s.title }))} value={source}
              onChange={(v) => { setSource(v); setColumns([]); setFilters({ op: "and", items: [] }); setPv(null); }} placeholder={t("Izaberite izvor")} />
            {src && <Text size="xs" c="dimmed" mt={4}>{cols.length} {t("kolona")} · {t("opseg korisnika (opštine/BPG) se primjenjuje automatski")}</Text>}
          </Card>
          {src && (
            <>
              <Card withBorder>
                <Group justify="space-between" mb="xs"><Title order={6}>2. {t("Kolone i agregacije")}</Title>
                  <Group gap={4}>
                    <Button size="compact-xs" variant="light" onClick={() => setColumns([...columns, { field: cols[0].field }])}>+ {t("kolona")}</Button>
                    <Button size="compact-xs" variant="light" onClick={() => setColumns([...columns, { expr: "", agg: "sum", label: "" }])}>+ {t("izraz")}</Button>
                  </Group></Group>
                <Stack gap={6}>
                  {columns.map((c, i) => (
                    <Group key={i} gap={4} wrap="nowrap">
                      {c.expr !== undefined
                        ? <TextInput size="xs" style={{ flex: 1 }} placeholder="npr. vrijednost_km / kolicina_kg" value={c.expr} onChange={(e) => setCol(i, { expr: e.currentTarget.value })} />
                        : <Select size="xs" style={{ flex: 1 }} searchable data={cols.map((x) => ({ value: x.field, label: x.label }))} value={c.field ?? null}
                          onChange={(v) => setCol(i, { field: v ?? undefined })} />}
                      <Select size="xs" w={130} data={AGGS} value={c.agg ?? ""} onChange={(v) => setCol(i, { agg: v || null })} />
                      <TextInput size="xs" w={150} placeholder={t("natpis")} value={c.label ?? ""} onChange={(e) => setCol(i, { label: e.currentTarget.value })} />
                      <ActionIcon size="sm" color="red" variant="subtle" onClick={() => setColumns(columns.filter((_, j) => j !== i))}><IconTrash size={12} /></ActionIcon>
                    </Group>
                  ))}
                  {!columns.length && <Text size="xs" c="dimmed">{t("Bez izbora — prikazuje se prvih 12 kolona izvora.")}</Text>}
                  <Text size="xs" c="dimmed">{t("Kolone bez agregacije postaju grupisanje kada postoji bar jedna agregacija. Izrazi: polja, brojevi, + − × /, round, abs, coalesce.")}</Text>
                </Stack>
              </Card>
              <Card withBorder>
                <Title order={6} mb="xs">3. {t("Uslovi (AND / OR / NOT)")}</Title>
                <CondEditor node={filters} cols={cols} params={params} onChange={setFilters} />
                {dateCols.length > 0 && <Select mt="xs" size="xs" label={t("Polje perioda (parametri od/do)")} clearable data={dateCols.map((x) => ({ value: x.field, label: x.label }))}
                  value={periodField} onChange={setPeriodField} />}
              </Card>
              <Card withBorder>
                <Group justify="space-between" mb="xs"><Title order={6}>4. {t("Parametri izvještaja")}</Title>
                  <Group gap={4}>
                    <Button size="compact-xs" variant="light" onClick={() => !params.some((p) => p.name === "od") && setParams([...params,
                      { name: "od", label: "Od datuma", type: "date" }, { name: "do", label: "Do datuma", type: "date" }])}>+ {t("period od/do")}</Button>
                    <Button size="compact-xs" variant="light" onClick={() => setParams([...params, { name: `p${params.length + 1}`, label: "Parametar", type: "text" }])}>+ {t("parametar")}</Button>
                  </Group></Group>
                {params.map((p, i) => (
                  <Group key={i} gap={4} mb={4} wrap="nowrap">
                    <TextInput size="xs" w={110} value={p.name} onChange={(e) => setParams(params.map((x, j) => (j === i ? { ...x, name: e.currentTarget.value.replace(/[^a-z0-9_]/g, "") } : x)))} />
                    <TextInput size="xs" style={{ flex: 1 }} value={p.label} onChange={(e) => setParams(params.map((x, j) => (j === i ? { ...x, label: e.currentTarget.value } : x)))} />
                    <Select size="xs" w={120} data={[{ value: "integer", label: t("cijeli broj") }, { value: "date", label: t("datum") }, { value: "text", label: t("tekst") }]}
                      value={p.type} onChange={(v) => setParams(params.map((x, j) => (j === i ? { ...x, type: v ?? "text" } : x)))} />
                    <ActionIcon size="sm" color="red" variant="subtle" onClick={() => setParams(params.filter((_, j) => j !== i))}><IconTrash size={12} /></ActionIcon>
                  </Group>
                ))}
              </Card>
              <Card withBorder>
                <Title order={6} mb="xs">5. {t("Sortiranje, grafik, ukupno")}</Title>
                <Group grow>
                  <Select size="xs" label={t("Sortiraj po")} clearable data={outCols.map((c) => ({ value: c.key, label: c.label }))} value={orderBy[0]?.field ?? null}
                    onChange={(v) => setOrderBy(v ? [{ field: v, dir: orderBy[0]?.dir ?? "desc" }] : [])} />
                  <Select size="xs" label={t("Smjer")} data={[{ value: "asc", label: t("rastuće") }, { value: "desc", label: t("opadajuće") }]} value={orderBy[0]?.dir ?? "desc"}
                    onChange={(v) => orderBy[0] && setOrderBy([{ ...orderBy[0], dir: v ?? "desc" }])} />
                  <NumberInput size="xs" label={t("Najviše redova")} value={limit} onChange={setLimit} max={50000} />
                </Group>
                <Checkbox mt="xs" label={t("Red sa ukupnim zbirom")} checked={totals} onChange={(e) => setTotals(e.currentTarget.checked)} />
                <Divider my="xs" />
                <Switch label={t("Grafik")} checked={!!chart} onChange={(e) => setChart(e.currentTarget.checked ? { type: "bar", x: outCols[0]?.key ?? null, series: [] } : null)} />
                {chart && <Group grow mt={4}>
                  <Select size="xs" label={t("Tip")} data={[{ value: "bar", label: t("stubičasti") }, { value: "line", label: t("linijski") }]} value={chart.type} onChange={(v) => setChart({ ...chart, type: v ?? "bar" })} />
                  <Select size="xs" label={t("X osa")} data={outCols.map((c) => ({ value: c.key, label: c.label }))} value={chart.x} onChange={(v) => setChart({ ...chart, x: v })} />
                  <MultiSelect size="xs" label={t("Serije")} data={outCols.map((c) => ({ value: c.key, label: c.label }))} value={chart.series} onChange={(v) => setChart({ ...chart, series: v })} />
                </Group>}
              </Card>
            </>
          )}
        </Stack>
      </Grid.Col>
      <Grid.Col span={{ base: 12, xl: 7 }}>
        <Stack>
          <Card withBorder>
            <Group justify="space-between" mb="xs"><Title order={6}>{t("Pregled rezultata")}</Title>
              <Button size="xs" onClick={preview} loading={busy} disabled={!source}>{t("Osvježi pregled")}</Button></Group>
            {params.length > 0 && <ParamsForm defs={params} values={pvParams} onChange={setPvParams} />}
          </Card>
          {pv && chart?.x && chart.series.length > 0 && <Card withBorder><Chart rows={pv.rows} x={chart.x} type={chart.type === "line" ? "line" : "bar"}
            series={chart.series.map((s) => ({ key: s, label: pv.columns.find((c) => c.key === s)?.label ?? s }))} /></Card>}
          {pv && <DataTable rows={pv.rows} totals={pv.totals} maxHeight={480} exportName="pregled"
            columns={pv.columns.map((c) => ({ key: c.key, label: c.label, type: c.type as "text" }))} />}
          <Card withBorder>
            <Title order={6} mb="xs">{t("Snimanje")}</Title>
            <Group grow>
              <TextInput label={t("Naziv")} required value={name} onChange={(e) => setName(e.currentTarget.value)} />
              <TextInput label={t("Kategorija")} value={category} onChange={(e) => setCategory(e.currentTarget.value)} />
              <Select label={t("Namijenjen")} data={[{ value: "oba", label: t("svima") }, { value: "proizvodjac", label: t("proizvođaču") }, { value: "agencija", label: t("Agenciji") }]}
                value={audience} onChange={(v) => setAudience(v ?? "oba")} />
            </Group>
            <Textarea mt="xs" label={t("Opis")} value={desc} onChange={(e) => setDesc(e.currentTarget.value)} autosize minRows={2} />
            <Group justify="space-between" mt="sm">
              {can("reports:manage") ? <Switch label={t("Dijeli sa svim korisnicima")} checked={shared} onChange={(e) => setShared(e.currentTarget.checked)} />
                : <Text size="xs" c="dimmed">{t("Izvještaj je lični (dijeljenje odobrava administrator izvještaja).")}</Text>}
              <Button onClick={save} disabled={!name || !source}>{template ? t("Sačuvaj izmjene") : t("Sačuvaj izvještaj")}</Button>
            </Group>
          </Card>
        </Stack>
      </Grid.Col>
    </Grid>
  );
}
